import React, { useState, useEffect } from 'react';
import { ageGroupsAPI } from '../../services/api';
import { AgeGroupSelect as AgeGroupSelectType } from '../../types/ageGroup';

interface AgeGroupSelectProps {
  value: number | null | undefined;
  onChange: (id: number | null, name?: string) => void;
  required?: boolean;
  autoSelectSingle?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const AgeGroupSelect: React.FC<AgeGroupSelectProps> = ({
  value,
  onChange,
  required = false,
  autoSelectSingle = true,
  disabled = false,
  className = '',
  placeholder = 'Válassz korosztályt',
}) => {
  const [ageGroups, setAgeGroups] = useState<AgeGroupSelectType[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoSelected, setAutoSelected] = useState(false);

  useEffect(() => {
    loadAgeGroups();
    // Szándékosan csak mount-kor fut: a lista egyszeri betöltése és az egyetlen
    // korosztály automatikus kiválasztása nem ismétlődhet minden renderkor
    // (az onChange prop gyakran inline függvény, így nem stabil referencia).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAgeGroups = async () => {
    try {
      setLoading(true);
      const data = await ageGroupsAPI.getForSelect();
      setAgeGroups(data);

      // Auto-select if only one age group and autoSelectSingle is true
      if (autoSelectSingle && data.length === 1 && !value) {
        onChange(data[0].id, data[0].name);
        setAutoSelected(true);
      }
    } catch (error) {
      console.error('Failed to load age groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value ? parseInt(e.target.value) : null;
    const selectedAgeGroup = ageGroups.find(ag => ag.id === newValue);
    onChange(newValue, selectedAgeGroup?.name);
    setAutoSelected(false);
  };

  // If auto-selected and only one option, disable the select
  const isEffectivelyDisabled = disabled || (autoSelected && ageGroups.length === 1);

  return (
    <select
      value={value ?? ''}
      onChange={handleChange}
      required={required}
      disabled={isEffectivelyDisabled || loading}
      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
        isEffectivelyDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
      } ${className}`}
    >
      {loading ? (
        <option value="">Betöltés...</option>
      ) : (
        <>
          {!autoSelected && <option value="">{placeholder}</option>}
          {ageGroups.map((ageGroup) => (
            <option key={ageGroup.id} value={ageGroup.id}>
              {ageGroup.name}
            </option>
          ))}
        </>
      )}
    </select>
  );
};

export default AgeGroupSelect;
